// React
import { useState } from "react";

// Third-Party
import CloseIcon from "@mui/icons-material/Close";
import { Autocomplete, Button, Chip, Stack, TextField } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";

// Internal
import { generalStyles, tagsFieldInputStyles } from "@utils/cssStyles";
import { filterChipStyles } from "@utils/filterDialogStyles";

interface IRHFTagsInputProps {
  name: string;
  placeholder?: string;
  addLabel?: string;
  options?: string[];
  disabled?: boolean;
  // Chips above the input with an explicit Add button, instead of chips inside the input
  showAddButton?: boolean;
  sx?: any;
}

interface ITagsFieldProps extends Omit<IRHFTagsInputProps, "name"> {
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
}

// Per-tag rules (e.g. max length) report on the item, not the array, so surface the first one
const getTagsErrorMessage = (error: any): string | undefined => {
  if (!error) return undefined;
  if (error.message) return error.message;
  if (Array.isArray(error)) return error.find((item) => item?.message)?.message;
  return undefined;
};

// Trim, drop empties and de-duplicate case-insensitively (first spelling wins)
const normalizeTags = (tags: string[]) => {
  const seen = new Set<string>();
  return tags.reduce<string[]>((acc, tag) => {
    const trimmed = tag.trim();
    const key = trimmed.toLowerCase();
    if (trimmed && !seen.has(key)) {
      seen.add(key);
      acc.push(trimmed);
    }
    return acc;
  }, []);
};

function TagsField({
  value,
  onChange,
  error,
  placeholder,
  addLabel,
  options = [],
  disabled,
  showAddButton,
  sx,
}: ITagsFieldProps) {
  const [inputValue, setInputValue] = useState("");

  const handleAdd = () => {
    onChange(normalizeTags([...value, inputValue]));
    setInputValue("");
  };

  return (
    <>
      {showAddButton && value.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 1.5 }}>
          {value.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              disabled={disabled}
              deleteIcon={<CloseIcon />}
              sx={filterChipStyles}
              onDelete={() => onChange(value.filter((t) => t !== tag))}
            />
          ))}
        </Stack>
      )}
      <Stack direction="row" gap={1} alignItems="flex-start">
        <Autocomplete
          multiple
          freeSolo
          fullWidth
          size="small"
          disabled={disabled}
          options={options}
          value={value}
          inputValue={inputValue}
          onInputChange={(_, newInput, reason) => {
            if (reason !== "reset") setInputValue(newInput);
          }}
          onChange={(_, newValue) => {
            onChange(normalizeTags(newValue as string[]));
            setInputValue("");
          }}
          renderTags={(tags, getTagProps) =>
            showAddButton
              ? null
              : tags.map((option, index) => (
                  <Chip
                    size="small"
                    variant="outlined"
                    deleteIcon={<CloseIcon />}
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                    sx={filterChipStyles}
                  />
                ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={value.length ? undefined : placeholder}
              disabled={disabled}
              error={!!error}
              helperText={error}
              sx={sx ?? tagsFieldInputStyles(!!error)}
            />
          )}
        />
        {showAddButton && (
          <Button
            variant="outlined"
            color="inherit"
            disabled={disabled || !inputValue.trim()}
            onClick={handleAdd}
            sx={{ ...generalStyles.flexCenterStyle, height: 40, px: 3 }}
          >
            {addLabel}
          </Button>
        )}
      </Stack>
    </>
  );
}

export default function RHFTagsInput({ name, ...other }: IRHFTagsInputProps) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TagsField
          {...other}
          value={field.value || []}
          onChange={field.onChange}
          error={getTagsErrorMessage(error)}
        />
      )}
    />
  );
}
